import { useState, useEffect, useMemo } from 'react'
import { functionsClient } from '../../../lib/functionsClient'
import type { DocumentClassification } from '../../../types/documentScanning'

export interface UseClassificationEditorResult {
  selectedType: string
  selectedYear: string
  isEditing: boolean
  hasChanges: boolean
  handleSave: () => Promise<void>
  handleEdit: () => void
  handleReset: () => void
  setSelectedType: (type: string) => void
  setSelectedYear: (year: string) => void
  setIsEditing: (editing: boolean) => void
  isSaving: boolean
}

export function useClassificationEditor(
  classification: DocumentClassification | null,
  docId: string | undefined,
  caseId: string | undefined,
  filename: string | undefined
): UseClassificationEditorResult {
  const [selectedType, setSelectedType] = useState<string>('')
  const [selectedYear, setSelectedYear] = useState<string>('')
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Initialize edit state when classification changes
  useEffect(() => {
    if (classification) {
      setSelectedType(classification.document_type || '')
      setSelectedYear(classification.tax_year?.toString() || '')
    } else {
      setSelectedType('')
      setSelectedYear('')
    }
    // Reset edit mode when switching documents
    setIsEditing(false)
  }, [classification])

  // Check if classification has changes
  const hasChanges = useMemo(() => {
    if (!classification) return false
    return (
      selectedType !== classification.document_type ||
      selectedYear !== (classification.tax_year?.toString() || '')
    )
  }, [classification, selectedType, selectedYear])

  // Save classification changes
  const handleSave = async () => {
    if (!docId || !caseId || !filename) return

    setIsSaving(true)
    try {
      // Use saveClassifications to persist changes
      await functionsClient.saveClassifications(caseId, [{
        filename,
        document_type: selectedType,
        tax_year: selectedYear ? parseInt(selectedYear, 10) : undefined,
        confidence: 1.0, // User edits are 100% confidence
      }])
    } catch (error) {
      console.error('Failed to save classification:', error)
      throw error // Re-throw so caller can handle
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleReset = () => {
    if (classification) {
      setSelectedType(classification.document_type || '')
      setSelectedYear(classification.tax_year?.toString() || '')
    }
    setIsEditing(false)
  }

  return {
    selectedType,
    selectedYear,
    isEditing,
    hasChanges,
    handleSave,
    handleEdit,
    handleReset,
    setSelectedType,
    setSelectedYear,
    setIsEditing,
    isSaving,
  }
}

